import { z } from 'zod';
import { ApiEndpoint } from 'app/agriculture/queries/apiEndpoints';
import { BucketSize } from 'app/agriculture/queries/snapshots/config/bucketConfig';
import { Snapshot } from 'app/agriculture/queries/snapshots/types/Snapshot';
import {
    PowerSnapshot,
    powerSnapshotSchema,
} from 'app/agriculture/queries/devices/types/PowerSnapshot';
import {
    NetworkSnapshot,
    networkSnapshotSchema,
} from 'app/agriculture/queries/devices/types/NetworkSnapshot';
import {
    WeatherSnapshot,
    weatherSnapshotSchema,
} from 'app/agriculture/queries/devices/types/WeatherSnapshot';
import {
    DiagnosticSnapshot,
    diagnosticSnapshotSchema,
} from 'app/agriculture/queries/devices/types/DiagnosticSnapshot';
import {
    FirmwareSnapshot,
    firmwareSnapshotSchema,
} from 'app/agriculture/queries/devices/types/FirmwareSnapshot';
import {
    TemperatureSnapshot,
    temperatureSnapshotSchema,
} from 'app/agriculture/queries/devices/types/TemperatureSnapshot';
import { HerdSnapshot, herdSnapshotSchema } from 'app/agriculture/queries/herds/types/HerdSnapshot';
import {
    ProtagAnimalSnapshot,
    protagAnimalSnapshotSchema,
} from 'app/agriculture/queries/protagAnimals/types/ProtagAnimalSnapshot';
import {
    LocationSnapshot,
    locationSnapshotSchema,
} from 'app/agriculture/queries/devices/types/LocationSnapshot';
import {
    ImageSnapshot,
    imageSnapshotSchema,
} from 'app/agriculture/queries/devices/types/ImageSnapshot';
import {
    LevelSnapshot,
    levelSnapshotSchema,
} from 'app/agriculture/queries/devices/types/LevelSnapshot';
import {
    HeartbeatSnapshot,
    heartbeatSnapshotSchema,
} from 'app/agriculture/queries/devices/types/HeartbeatSnapshot';
import { Duration } from 'date-fns';

export const snapshotConfig = {
    Network: {
        endpoint: ApiEndpoint.networkSnapshots,
        bucketSize: 'hour',
        shelfLife: { hours: 1 },
        parseSnapshots: (data: unknown[]) =>
            z.array(networkSnapshotSchema).parse(data) as NetworkSnapshot[],
    },
    Power: {
        endpoint: ApiEndpoint.powerSnapshots,
        bucketSize: 'hour',
        shelfLife: { hours: 1 },
        parseSnapshots: (data: unknown) =>
            z.array(powerSnapshotSchema).parse(data) as PowerSnapshot[],
    },
    Weather: {
        endpoint: ApiEndpoint.weatherSnapshots,
        bucketSize: 'hour',
        shelfLife: { hours: 1 },
        parseSnapshots: (data: unknown) =>
            z.array(weatherSnapshotSchema).parse(data) as WeatherSnapshot[],
    },
    Diagnostic: {
        endpoint: ApiEndpoint.diagnosticSnapshots,
        bucketSize: 'hour',
        shelfLife: { hours: 1 },
        parseSnapshots: (data: unknown) =>
            z.array(diagnosticSnapshotSchema).parse(data) as DiagnosticSnapshot[],
    },
    Firmware: {
        endpoint: ApiEndpoint.firmwareSnapshots,
        bucketSize: 'month',
        shelfLife: null,
        parseSnapshots: (data: unknown) =>
            z.array(firmwareSnapshotSchema).parse(data) as FirmwareSnapshot[],
    },
    Temperature: {
        endpoint: ApiEndpoint.temperatureSnapshots,
        bucketSize: 'month',
        shelfLife: null,
        parseSnapshots: (data: unknown) =>
            z.array(temperatureSnapshotSchema).parse(data) as TemperatureSnapshot[],
    },
    Herd: {
        endpoint: ApiEndpoint.herdSnapshots,
        bucketSize: 'week',
        shelfLife: null,
        parseSnapshots: (data: unknown) =>
            z.array(herdSnapshotSchema).parse(data) as HerdSnapshot[],
    },
    ProtagAnimal: {
        endpoint: ApiEndpoint.protagAnimalSnapshots,
        bucketSize: 'hour',
        shelfLife: { hours: 1 },
        parseSnapshots: (data: unknown) =>
            z.array(protagAnimalSnapshotSchema).parse(data) as ProtagAnimalSnapshot[],
    },
    Location: {
        endpoint: ApiEndpoint.locationSnapshots,
        bucketSize: 'hour',
        shelfLife: null,
        parseSnapshots: (data: unknown) =>
            z.array(locationSnapshotSchema).parse(data) as LocationSnapshot[],
    },
    Image: {
        endpoint: ApiEndpoint.imageSnapshots,
        bucketSize: 'day',
        shelfLife: null,
        parseSnapshots: (data: unknown) =>
            z.array(imageSnapshotSchema).parse(data) as ImageSnapshot[],
    },
    Level: {
        endpoint: ApiEndpoint.levelSnapshots,
        bucketSize: 'hour',
        shelfLife: { hours: 1 },
        parseSnapshots: (data: unknown) =>
            z.array(levelSnapshotSchema).parse(data) as LevelSnapshot[],
    },
    GasbotLevel: {
        endpoint: ApiEndpoint.gasbotLevelSnapshots,
        bucketSize: 'week',
        shelfLife: { hours: 12 },
        parseSnapshots: (data: unknown) =>
            z.array(levelSnapshotSchema).parse(data) as LevelSnapshot[],
    },
    Heartbeat: {
        endpoint: ApiEndpoint.heartbeatSnapshots,
        bucketSize: 'hour',
        shelfLife: { hours: 1 },
        parseSnapshots: (data: unknown) =>
            z.array(heartbeatSnapshotSchema).parse(data) as HeartbeatSnapshot[],
    },
} as const satisfies Record<
    string,
    {
        bucketSize: BucketSize;
        endpoint: string;
        shelfLife: Duration | null;
        parseSnapshots: (data: unknown[]) => Snapshot[];
    }
>;

export type SnapshotType = keyof typeof snapshotConfig;
export type SnapshotInterface<T extends SnapshotType> = ReturnType<
    (typeof snapshotConfig)[T]['parseSnapshots']
>[number];
export const allSnapshotTypes = Object.keys(snapshotConfig) as SnapshotType[];

/**
 * a subset of snapshot types that we want to have prefetched whenever the timeline interval changes
 *
 * we mostly only care about snapshots that drive the map rendering in some way
 * notable exclusions: Temperature, Diagnostic, Firmware, Image
 */
export const prefetchedSnapshotTypes: SnapshotType[] = [
    'Location',
    'Herd',
    'Level',
    'GasbotLevel',
    'Heartbeat',
    'Network',
    'Power',
    'ProtagAnimal',
    'Weather',
];
